import {
  KuxFilteredCategoriesDTO,
  MigrateOperation,
  PageStoreSubTypes,
  PREDEFIENED_REFERENCES,
  sendMigrationEvent,
  UIDAPresentationTypeEnum,
  UIDAStructureTypeEnum,
} from '@kaltura-ott/tvpil';
import { observable } from 'mobx';
import { container } from 'tsyringe';

import type { AppStore } from '../../app/app-store';
import { LastWatchedLinearStore } from '../../channelStats/last-watched-linear-store';
import type { ParentalPinStore } from '../../parentalPin';
import { RecordRepository } from '../../recordRepository';
import { SearchStore } from '../../search';
import type { UserStore } from '../../user';
import { getFormatedMacAddress } from '../../user/login-service';
import { GridStore } from '../aside';
import { CategoryChildren } from '../category-children';
import { CategoryStore } from '../category-store';
import { AssetDetailsPageStore } from '../details';
import { FooterStore } from '../footer';
import { LoginStore } from '../login';
import { MenuStore } from '../menu';
import { PlayerStore } from '../player';
import {
  DeviceSubpageStore,
  FavoritesStore,
  ParentalControlStore,
  SelfServiceStore,
  SubpageStore,
  SubscriptionStore,
  VideoStore,
} from '../subpage';
import {
  AppsRailStoreV2,
  ChannelApiRailStoreV2,
  CollectionRailStoreV2,
  ContinueRailStoreV2,
  HeroRailStoreV2,
  RecommendationsRailStoreV2,
  RecordsStoreV2,
  SeriesRailStoreV2,
} from '../widgets';
import { RecordAdditionalRailStoreV2 } from '../widgets/rails/record-rail/record-rail-additional-store';
import { TotalRecordsRailStore } from '../widgets/rails/record-rail/total-records-rail-store';
import { RentalsRailStoreV2 } from '../widgets/rails/rentals-rail/rentals-rail-store-v2';
import { ZapperStore } from '../zapper';

import { EpgStore } from './epg-store';

/**
 * Page category store
 * @category Store
 */
export class PageStore extends CategoryStore<CategoryChildren> {
  type: UIDAStructureTypeEnum.page = UIDAStructureTypeEnum.page;
  subtype: PageStoreSubTypes = UIDAPresentationTypeEnum.container;

  @observable presentPlayer: boolean;
  @observable isReady: boolean;
  @observable scheduledRecordingSingleRail?: RecordAdditionalRailStoreV2;
  @observable totalRecordsRail?: TotalRecordsRailStore;

  private recordRepository: RecordRepository;
  private appStore: AppStore;
  private lastWatchedLinear: LastWatchedLinearStore;

  // TODO NTD https://kaltura.atlassian.net/wiki/spaces/KUXDEV/pages/1728414630/Deep+Link
  link: string;

  constructor(
    categoryItem: KuxFilteredCategoriesDTO,
    parentCategoryTree: KuxFilteredCategoriesDTO,
    children: CategoryChildren[] = [],
  ) {
    super(categoryItem, parentCategoryTree);

    this.subtype = categoryItem.subtype as PageStoreSubTypes;
    this.children = children;
    if ([UIDAPresentationTypeEnum.container, UIDAPresentationTypeEnum.applications].includes(this.subtype)) {
      this.setupRootMenu(parentCategoryTree);
      this.setupRootMenu(parentCategoryTree, UIDAPresentationTypeEnum.footer);
    }
    this.link = `/page/${this.referenceId}`;
    this.presentPlayer = categoryItem.presentPlayer;
    this.recordRepository = container.resolve<RecordRepository>('recordRepository');
    this.appStore = container.resolve<AppStore>('appStore');
    this.lastWatchedLinear = container.resolve<LastWatchedLinearStore>('lastWatchedLinearStore');
    this.isReady = this.subtype !== UIDAPresentationTypeEnum.npvr;
  }

  /**
   * Should be called before visiting the page
   */
  async init(): Promise<void> {
    const parentalPinStore = container.resolve<ParentalPinStore>('parentalPinStore');
    parentalPinStore.setLastPageLink(!this.parentalPinProtected ? '' : this.referenceId);
    if (this.subtype === UIDAPresentationTypeEnum.npvr) {
      await this.appStore.reInitRecordings();

      if (this.appStore.rootStore?.seriesRecordingPageTree) {
        this.scheduledRecordingSingleRail = new RecordAdditionalRailStoreV2(
          this.appStore.rootStore.seriesRecordingPageTree.children[0],
          this.appStore.rootStore.seriesRecordingPageTree,
        );
      } else {
        console.warn('Rail is not configured in OPC!!');
      }

      // totalRecordsRail
      this.totalRecordsRail = new TotalRecordsRailStore(
        this.children[0].categoryTree,
        this.categoryTree,
        undefined,
        this.scheduledRecordingSingleRail,
      );
    }
    if (this.isHomepage) {
      await this.appStore.refreshData();
      const macForMigration = this.appStore.params.silentLoginParams?.macLan;
      sendMigrationEvent(MigrateOperation.HOMEPAGE, {
        isSTBMigrationOperation: this.appStore.dms.isSTBMigrationOperation,
        stbMigrationUrl: this.appStore.dms.stbMigrationUrl,
        mac: getFormatedMacAddress(macForMigration),
        firstSTBApplySettings: this.appStore.loginStore.originalStore.firstSTBAppliedSettings,
      });
      this.appStore.loginStore.originalStore.firstSTBAppliedSettings = false;
    }
    this.isReady = true;
  }

  async initRecordsPage() {
    await this.init();
    await this.totalRecordsRail?.init();
  }

  setupRootMenu(parentCategoryTree: KuxFilteredCategoriesDTO, rootSubtype = UIDAPresentationTypeEnum.menu) {
    const isMenuExist = this.children?.find(
      ({ type, subtype }) => type === UIDAStructureTypeEnum.menu && subtype === rootSubtype,
    );

    if (!isMenuExist) {
      const rootMenuTree = parentCategoryTree?.children.find(
        ({ type, subtype }) => type === UIDAStructureTypeEnum.menu && subtype === rootSubtype,
      );

      if (rootMenuTree) {
        const userStore = container.resolve<UserStore>('userStore');

        const menuChildren = getChildren(rootMenuTree, userStore.customerType);
        if (rootSubtype === UIDAPresentationTypeEnum.menu) {
          this.children?.unshift(new MenuStore(rootMenuTree, parentCategoryTree, menuChildren));
        } else {
          this.children?.push(new FooterStore(rootMenuTree, parentCategoryTree, menuChildren));
        }
      }
    }
  }

  get isHomepage(): boolean {
    return this.referenceId === PREDEFIENED_REFERENCES.home;
  }

  /**
   * Should be called after leaving the page
   */
  public resetStore(): void {
    this.isReady = this.subtype !== UIDAPresentationTypeEnum.npvr;
  }
}

/**
 * @category Store
 */
export class ApplicationsStore extends PageStore {
  type: UIDAStructureTypeEnum.page = UIDAStructureTypeEnum.page;
  subtype: UIDAPresentationTypeEnum.applications = UIDAPresentationTypeEnum.applications;
}

/**
 * Validate rail property before set to children
 *
 * @param categoryTree: KuxFilteredCategoriesDTO
 */
const isValidRail = (categoryTree: KuxFilteredCategoriesDTO): boolean => {
  const railsWithHeight = [
    UIDAPresentationTypeEnum.hero,
    UIDAPresentationTypeEnum.cw,
    UIDAPresentationTypeEnum.collection,
    UIDAPresentationTypeEnum.suggested,
    UIDAPresentationTypeEnum.series_catch_up,
    UIDAPresentationTypeEnum.grid,
    UIDAPresentationTypeEnum.tvod,
  ];

  if (
    railsWithHeight.includes(categoryTree.subtype) &&
    (!categoryTree.railImageProperty.assetImageHeight || !categoryTree.railImageProperty.assetPresentationRatio)
  ) {
    console.log('Height is not defined', categoryTree);

    return false;
  }
  return true;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getChildren = (categoryTree: KuxFilteredCategoriesDTO, customerType: number | string): any => {
  const WIDGET_BY_SUBTYPE = {
    [UIDAPresentationTypeEnum.hero]: HeroRailStoreV2,
    [UIDAPresentationTypeEnum.cw]: ContinueRailStoreV2,
    [UIDAPresentationTypeEnum.npvr]: RecordsStoreV2,
    [UIDAPresentationTypeEnum.collection]: CollectionRailStoreV2,
    [UIDAPresentationTypeEnum.apps]: AppsRailStoreV2,
    [UIDAPresentationTypeEnum.zapper]: ZapperStore,
    [UIDAPresentationTypeEnum.channel_api]: ChannelApiRailStoreV2,
    [UIDAPresentationTypeEnum.suggested]: RecommendationsRailStoreV2,
    [UIDAPresentationTypeEnum.series_catch_up]: SeriesRailStoreV2,
    [UIDAPresentationTypeEnum.tvod]: RentalsRailStoreV2,
  };

  const MENU_BY_SUBTYPE = {
    [UIDAPresentationTypeEnum.menu]: MenuStore,
    [UIDAPresentationTypeEnum.footer]: FooterStore,
  };

  return categoryTree.children
    .map((child) => {
      const userTypeDenial = child.subpageProperty?.userTypeDenialListForUiComponents ?? [];
      if (userTypeDenial.includes(customerType.toString())) {
        return;
      }
      switch (child.type) {
        case UIDAStructureTypeEnum.page: {
          switch (child.subtype) {
            case UIDAPresentationTypeEnum.settings:
            case UIDAPresentationTypeEnum.container:
            case UIDAPresentationTypeEnum.npvr: {
              const children = getChildren(child, customerType);
              return new PageStore(child, categoryTree, children);
            }
            case UIDAPresentationTypeEnum.epg: {
              const children: CategoryChildren[] = getChildren(child, customerType);
              return new EpgStore(child, categoryTree, children);
            }

            default:
              return null;
          }
        }
        case UIDAStructureTypeEnum.menu: {
          switch (child.subtype) {
            case UIDAPresentationTypeEnum.menu:
            case UIDAPresentationTypeEnum.footer: {
              const children = getChildren(child, customerType);
              return new MENU_BY_SUBTYPE[child.subtype](child, categoryTree, children);
            }
            default:
              return null;
          }
        }
        case UIDAStructureTypeEnum.widget: {
          // @ts-expect-error TODO Fix
          const widget = WIDGET_BY_SUBTYPE[child.subtype];

          if (widget) {
            const children = getChildren(child, customerType);
            if (isValidRail(child)) {
              return new widget(child, categoryTree, children);
            } else {
              return null;
            }
          }

          return null;
        }
        case UIDAStructureTypeEnum.subpage: {
          const children = getChildren(child, customerType);
          switch (child.subtype) {
            case UIDAPresentationTypeEnum.subscription:
              return new SubscriptionStore(child, categoryTree, children);
            case UIDAPresentationTypeEnum.parental_control:
              return new ParentalControlStore(child, categoryTree, children);
            case UIDAPresentationTypeEnum.link_list:
              return new SelfServiceStore(child, categoryTree, children);
            case UIDAPresentationTypeEnum.preferred_video_quality:
              return new VideoStore(child, categoryTree, children);
            case UIDAPresentationTypeEnum.favorites:
              return new FavoritesStore(child, categoryTree, children);
            case UIDAPresentationTypeEnum.devices:
              return new DeviceSubpageStore(child, categoryTree, children);
          }
          return new SubpageStore(child, categoryTree, children);
        }
        case UIDAStructureTypeEnum.root:
          // TODO: add zapper and login for subtype: folder
          return null;
        case UIDAStructureTypeEnum.aside:
          // not required for technical enum
          return null;
        default:
          console.log(`${child.subtype} (${child.type}) is not defined`, child);
          return null;
      }
    })
    .filter(Boolean);
};

/**
 * Finds aside footer from category tree.
 * @param categoryTree
 * @returns aside footer with related stores
 */
export const getAsideFolder = (categoryTree: KuxFilteredCategoriesDTO, customerType: number | string) => {
  const asideFolder = categoryTree.children.find((child) => child.type === UIDAStructureTypeEnum.aside);

  if (asideFolder) {
    const zapperTree = asideFolder.children.find((aside) => aside.subtype === UIDAPresentationTypeEnum.zapper);
    const loginTree = asideFolder.children.find((aside) => aside.subtype === UIDAPresentationTypeEnum.login);
    const applicationsTree = asideFolder.children.find(
      (aside) => aside.subtype === UIDAPresentationTypeEnum.applications,
    );
    const assetDetailsPageTrees = asideFolder.children.filter(
      (aside) => aside.subtype === UIDAPresentationTypeEnum.asset_details_page,
    );
    const gridsTree = asideFolder.children.filter((aside) => aside.subtype === UIDAPresentationTypeEnum.grid);
    const searchTree = asideFolder.children.find((aside) => aside.subtype === UIDAPresentationTypeEnum.search);
    const topShelfTree = asideFolder.children.find((aside) => aside.subtype === UIDAPresentationTypeEnum.top_shelf);
    const seriesRecordingPageTree = asideFolder.children.find(
      (aside) => aside.subtype === UIDAPresentationTypeEnum.series_recordings,
    );
    const playerTree = asideFolder.children.find((aside) => aside.subtype === UIDAPresentationTypeEnum.player);

    return {
      seriesRecordingPageTree: seriesRecordingPageTree,
      zapper: zapperTree && new ZapperStore(zapperTree),
      login: loginTree && new LoginStore(loginTree),
      applications:
        applicationsTree &&
        new ApplicationsStore(applicationsTree, categoryTree, getChildren(applicationsTree, customerType)),
      assetDetailsPages: assetDetailsPageTrees && new AssetDetailsPageStore(assetDetailsPageTrees, categoryTree),
      grid: gridsTree && new GridStore(gridsTree),
      search: searchTree && new SearchStore(searchTree),
      topShelf: topShelfTree,
      player: playerTree && new PlayerStore(playerTree),
    };
  }
  return {};
};
